import { flow, types } from 'mobx-state-tree'
import { getMidPayoutConfig } from '../../network/getMidPayoutConfig'
import { getMidPayoutLiabilityConfig } from '../../network/getMidPayoutLiabilityConfig'
import { merchantPayoutConfigProcessApprovals } from '../../network/merchantPayoutConfigProcessApprovals'
import { updateMidPayoutConfigReq } from '../../network/updateMidPayoutConfig'
import { updateMidPayoutLiabilityConfigReq } from '../../network/updateMidPayoutLiabilityConfig'
import { MessageTypes, getErrorMessage } from '../../utils'
import { RootStore } from '../store'

export const Config = types.model('Config', {
  mid: types.optional(types.string, ''),
  frequencyId: types.optional(types.string, ''),
  autoSettle: types.optional(types.boolean, false),
  autoSettleDays: types.optional(types.string, ''),
  settlementOnHold: types.optional(types.boolean, false),
  isDynamicSettlement: types.optional(types.boolean, false),
  remarks: types.optional(types.string, ''),
})

const LiabilityConfig = types.model('LiabilityConfig', {
  id: types.optional(types.number, -1),
  allowLiability: types.optional(types.boolean, false),
  liabilityLimit: types.optional(types.string, ''),
})
const MerchantPayoutConfig = types
  .model('MerchantPayoutConfig', {
    liabilityConfig: types.optional(LiabilityConfig, {}),
    config: types.optional(Config, {}),
  })
  .actions((self) => ({
    fetchMidPayoutConfig: flow(function* fetchMidPayoutConfig(mid: any) {
      try {
        const response = yield getMidPayoutConfig(mid, RootStore.session.getTokens)
        self.config = {
          ...response,
          mid: mid,
        }
        return response
      } catch (error) {
        self.config = {} as any
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        // if (getErrorMessage(error) === 'Payout configuration details not found') {
        //   return error
        // }
        throw error
      }
    }),
    fetchMidPayoutLiabilityConfig: flow(function* fetchMidPayoutLiabilityConfig(mid: number) {
      try {
        const response = yield getMidPayoutLiabilityConfig(mid, RootStore.session.getTokens)
        self.liabilityConfig = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateMidPayoutConfig: flow(function* updateMidPayoutConfigApi(mid: string, data: any, isAdd: boolean) {
      try {
        const response = yield updateMidPayoutConfigReq(mid, data, RootStore.session.getTokens, isAdd)
        RootStore.message.setMessage(response?.message ?? 'Update Success', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateMidPayoutLiabilityConfig: flow(function* updateMidPayoutLiabilityConfig(mid: string, data: any) {
      try {
        const response = yield updateMidPayoutLiabilityConfigReq(mid, data, RootStore.session.getTokens)
        self.liabilityConfig = response
        RootStore.message.setMessage(response?.message ?? 'Update Success', MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield merchantPayoutConfigProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    bulkApprovals: flow(function* (approveList: any) {
      const errorData: any = []
      for (let i = 0; i < approveList.length; i++) {
        const data = approveList[i]
        try {
          const response = yield merchantPayoutConfigProcessApprovals(data, RootStore.session.getTokens)
          errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
    bulkPayoutConfigUpdate: flow(function* (txns: any, isAdd: boolean) {
      const errorData: any = []
      for (let i = 0; i < txns.length; i++) {
        const data = txns[i]
        try {
          let response
          response = yield updateMidPayoutConfigReq(data.mid, data, RootStore.session.getTokens, isAdd, true)

          errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
    bulkLiabilityUpdate: flow(function* (txns: any, isAdd: boolean) {
      const errorData: any = []
      for (let i = 0; i < txns.length; i++) {
        const data = txns[i]
        try {
          let response
          response = yield updateMidPayoutLiabilityConfigReq(data?.mid, data, RootStore.session.getTokens, isAdd, true)

          errorData.push({ status: 'SUCCESS', message: response?.message, code: '' })
        } catch (e) {
          errorData.push({ status: 'Failure', message: getErrorMessage(e), code: (e as any)?.data?.error?.code ?? '' })
        }
        yield new Promise((r) => setTimeout(r, 200))
      }
      return errorData
    }),
  }))
  .views((self) => ({
    getAllConfigs() {
      return {
        ...self.config,
        mid: undefined,
      }
    },
    getLiabilityConfig() {
      return {
        ...self.liabilityConfig,
        mid: undefined,
      }
    },
  }))

const merchantPayoutConfigData = MerchantPayoutConfig.create({
  liabilityConfig: {},
  config: {},
})

export { MerchantPayoutConfig, merchantPayoutConfigData }
